/**
 * Regras de formulário Super Admin ↔ Pagar.me (recebedor PJ).
 * Fonte: regras de negócio Weeduc + criação do recebedor.
 */

function digits(value) {
    return String(value ?? '').replace(/\D/g, '');
}

function isBlank(value) {
    return value == null || String(value).trim() === '';
}

function parseMoney(value) {
    if (value == null || value === '') return NaN;
    const normalized = String(value).trim().replace(',', '.');
    return Number(normalized);
}

/** 'yes' | 'no' | 'unset' — sem venda, banco e split não são obrigatórios. */
export function salesIntent(form = {}) {
    const raw = form.hasSales;
    if (raw === 0 || raw === '0' || raw === false) return 'no';
    if (raw === 1 || raw === '1' || raw === true) return 'yes';
    return 'unset';
}

/** Campos de KYC da empresa que a Pagar.me praticamente não altera depois de criados. */
export const KYC_FIELD_KEYS = [
    'tradeName',
    'name',
    'idFiscal',
    'email',
    'phone',
    'postcode',
    'address',
    'addressNumber',
    'neighborhood',
    'city',
    'county',
];

/** Campos do responsável legal (editáveis no Weeduc; a Pagar.me pode rejeitar alterações). */
export const REPRESENTATIVE_FIELD_KEYS = [
    'nameRepresentative',
    'emailRepresentative',
    'documentRepresentative',
    'birthdateRepresentative',
    'occupationRepresentative',
];

/** Conta bancária: ainda pode tentar alterar se o titular for o mesmo CNPJ. */
export const BANK_FIELD_KEYS = [
    'bank',
    'branch',
    'digitBranch',
    'account',
    'digitAccount',
];

/** Textos dos tooltips (ícone fa-circle-exclamation). */
export const FIELD_TIPS = {
    tradeName:
        'Obrigatório para o recebedor. Depois de criado na Pagar.me, praticamente imutável.',
    name:
        'Obrigatório para o recebedor. Depois de criado na Pagar.me, praticamente imutável.',
    idFiscal:
        'Obrigatório (14 dígitos). Imutável após criar o recebedor. Outro CNPJ exige nova empresa.',
    email:
        'Obrigatório. Depois de criado o recebedor, praticamente imutável na Pagar.me.',
    phone:
        'Obrigatório (DDD + mínimo 10 dígitos). Depois de criado o recebedor, praticamente imutável.',
    postcode: 'Obrigatório (8 dígitos) para criar o recebedor Pagar.me.',
    address:
        'Obrigatório. Endereço e telefone do responsável reusam os da empresa.',
    addressNumber: 'Obrigatório. A Pagar.me exige street_number.',
    addressComplement: 'Se vazio, o Weeduc envia "Sem complemento".',
    neighborhood: 'Obrigatório para criar o recebedor Pagar.me.',
    city: 'Obrigatório para criar o recebedor Pagar.me.',
    county: 'Obrigatório (UF) para criar o recebedor Pagar.me.',
    country: 'Só Weeduc — não é enviado à Pagar.me.',
    bank:
        'Obrigatório para o recebedor. Depois, só troca conta do mesmo CNPJ.',
    branch:
        'Obrigatório para o recebedor. Depois, só troca conta do mesmo CNPJ.',
    digitBranch: 'Pode ficar vazio — a Pagar.me aceita sem dígito da agência.',
    account:
        'Obrigatório para o recebedor. Depois, só troca conta do mesmo CNPJ.',
    digitAccount:
        'Obrigatório para o recebedor. Depois, só troca conta do mesmo CNPJ.',
    split:
        'Obrigatório no Weeduc — sem split as compras não passam. Não vai à Pagar.me.',
    percentualClient:
        'Obrigatório no Weeduc para o checkout. Não vai à Pagar.me.',
    transactionValue:
        'Obrigatório no Weeduc (taxa) — sem isso as compras não passam. Não vai à Pagar.me.',
    nameRepresentative:
        'Obrigatório para o recebedor. Pode editar aqui; a Pagar.me pode rejeitar a alteração.',
    emailRepresentative:
        'Obrigatório para o recebedor. Pode editar aqui; a Pagar.me pode rejeitar a alteração.',
    documentRepresentative:
        'Obrigatório (11 dígitos). Pode editar aqui; a Pagar.me pode rejeitar a alteração.',
    birthdateRepresentative:
        'Obrigatório para o recebedor. Pode editar aqui; a Pagar.me pode rejeitar a alteração.',
    occupationRepresentative:
        'Obrigatório para o recebedor. Pode editar aqui; a Pagar.me pode rejeitar a alteração.',
    typeSystem: '1 = Weeduc, 2 = Weflix. Define o produto da empresa. Não vai à Pagar.me.',
    typeBusiness: 'Só Weeduc: 1 = Escolar, 2 = Corporativo. Não vai à Pagar.me.',
    testPlan: 'Sim = empresa em plano de teste; Não = plano normal. Só Weflix.',
    cartStatus: '1 = carrinho ativo, 0 = inativo. Sem vendas o sistema envia 2 e não cria recebedor na Pagar.me.',
    anticipation: 'Habilita antecipação de recebíveis na plataforma. Só Weeduc.',
    videoComments: 'Permite comentários nos vídeos. Só Weflix.',
    intallmentCheckout: 'Permite parcelamento no checkout. Só Weeduc.',
    color: 'Cor principal da marca na plataforma. Só Weeduc.',
    videoTimeTotal: 'Cota total de vídeo (minutos). Campo só Weeduc. Não é enviado à Pagar.me.',
    videoTimeUsed: 'Minutos de vídeo já consumidos. Campo só Weeduc. Não é enviado à Pagar.me.',
    ageRestricted: 'Sim = conteúdo com restrição de idade; Não = sem restrição. Só Weeduc.',
    idFolderVideoteca: 'UUID da pasta na Videoteca. Opcional — pode ser criada depois.',
    tokenVideoteca: 'Token da conta Videoteca. Opcional na criação; liga a conta e permite criar a pasta.',
    recipientCode: 'Código do recebedor Pagar.me. Deixe vazio na criação; gerado automaticamente.',
};

export function fieldTip(key, { kycLocked = false } = {}) {
    const base = FIELD_TIPS[key] || '';
    if (kycLocked && KYC_FIELD_KEYS.includes(key)) {
        return `${base} Bloqueado: recebedor já criado.`;
    }
    return base;
}

/**
 * Valida payload mínimo para criar/atualizar recebedor Pagar.me.
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validatePagarmeRequired(form = {}) {
    const errors = [];
    const idFiscal = digits(form.idFiscal);
    const phone = digits(form.phone);
    const postcode = digits(form.postcode);
    const cpf = digits(form.documentRepresentative ?? form.idRepresentative);

    if (isBlank(form.tradeName)) errors.push('Razão social (tradeName)');
    if (isBlank(form.name)) errors.push('Nome fantasia (name)');
    if (idFiscal.length !== 14) errors.push('CNPJ com 14 dígitos');
    if (isBlank(form.email)) errors.push('E-mail da empresa');
    if (phone.length < 10) errors.push('Telefone com DDD (mínimo 10 dígitos)');
    if (postcode.length !== 8) errors.push('CEP com 8 dígitos');
    if (isBlank(form.address)) errors.push('Endereço (rua)');
    if (isBlank(form.addressNumber)) errors.push('Número do endereço');
    if (isBlank(form.neighborhood)) errors.push('Bairro');
    if (isBlank(form.city)) errors.push('Cidade');
    if (isBlank(form.county)) errors.push('UF (estado)');
    if (salesIntent(form) === 'yes') {
        if (isBlank(form.bank)) errors.push('Banco (código)');
        if (isBlank(form.branch)) errors.push('Agência');
        if (isBlank(form.account)) errors.push('Conta');
        if (isBlank(form.digitAccount)) errors.push('Dígito da conta');
    }

    if (isBlank(form.nameRepresentative)) errors.push('Nome do responsável');
    if (isBlank(form.emailRepresentative)) errors.push('E-mail do responsável');
    if (cpf.length !== 11) errors.push('CPF do responsável com 11 dígitos');
    if (isBlank(form.birthdateRepresentative ?? form.birthdate)) {
        errors.push('Data de nascimento do responsável');
    }
    if (isBlank(form.occupationRepresentative ?? form.professional_occupation)) {
        errors.push('Ocupação profissional do responsável');
    }

    return { ok: errors.length === 0, errors };
}

/**
 * Split / % cliente / taxa: só Weeduc, mas obrigatórios para as compras funcionarem.
 * @returns {{ ok: boolean, errors: string[] }}
 */
export function validateWeeducCommerceRequired(form = {}) {
    const errors = [];
    if (salesIntent(form) === 'no') {
        return { ok: true, errors };
    }
    const splitRaw = form.split;
    const splitOk =
        splitRaw === 0
        || splitRaw === 1
        || splitRaw === '0'
        || splitRaw === '1';

    if (!splitOk) {
        errors.push('Split (ativo ou inativo)');
    }

    if (isBlank(form.percentualClient) || Number.isNaN(Number(form.percentualClient))) {
        errors.push('Percentual do cliente (%)');
    }

    const fee = parseMoney(form.transactionValue);
    if (isBlank(form.transactionValue) || Number.isNaN(fee)) {
        errors.push('Taxa / valor por transação (R$)');
    }

    return { ok: errors.length === 0, errors };
}

/**
 * Validação completa na criação/salvamento da empresa (Pagar.me + comércio Weeduc).
 */
export function validateCompanyForm(form = {}) {
    const pagarme = validatePagarmeRequired(form);
    const commerce = validateWeeducCommerceRequired(form);
    const errors = [...pagarme.errors, ...commerce.errors];
    return { ok: errors.length === 0, errors, pagarme, commerce };
}

export function bankSnapshot(form = {}) {
    return BANK_FIELD_KEYS.map((key) => String(form[key] ?? '').trim()).join('|');
}

export function kycSnapshot(form = {}) {
    return KYC_FIELD_KEYS.map((key) => String(form[key] ?? '').trim()).join('|');
}

export function representativeSnapshot(form = {}) {
    return REPRESENTATIVE_FIELD_KEYS.map((key) => String(form[key] ?? '').trim()).join('|');
}

export default {
    digits,
    isBlank,
    KYC_FIELD_KEYS,
    REPRESENTATIVE_FIELD_KEYS,
    BANK_FIELD_KEYS,
    FIELD_TIPS,
    fieldTip,
    salesIntent,
    validatePagarmeRequired,
    validateWeeducCommerceRequired,
    validateCompanyForm,
    bankSnapshot,
    kycSnapshot,
    representativeSnapshot,
};
